import type { Booking } from '../types/profile';

type Payload = Record<string, any>;

const str = (v: unknown, fallback = ''): string =>
    v === null || v === undefined ? fallback : String(v);

const firstString = (...candidates: unknown[]): string => {
    for (const c of candidates) {
        const s = str(c).trim();
        if (s) return s;
    }
    return '';
};

const isoDate = (...candidates: unknown[]): string => {
    for (const c of candidates) {
        const raw = str(c).trim();
        if (!raw) continue;
        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) return d.toISOString();
    }
    return '';
};

/** Places appear as either a bare code or a nested object, depending on source. */
const placeCode = (place: unknown): string => {
    if (!place) return '';
    if (typeof place === 'string') return place;
    const p = place as Payload;
    return firstString(p.iata_code, p.code, p.iata, p.city_name, p.city, p.name);
};

const placeName = (place: unknown): string => {
    if (!place) return '';
    if (typeof place === 'string') return place;
    const p = place as Payload;
    return firstString(p.city_name, p.city, p.name, p.iata_code, p.code);
};

/**
 * Maps an `order_confirmation` payload onto a Booking for the profile.
 *
 * The contract declares `slices` as free-form objects, so every field is
 * probed across the naming variants the gateway has been seen to emit
 * rather than assuming one provider's shape. Returns null when there is
 * no usable reference, so a malformed payload never creates a ghost trip.
 */
export const bookingFromOrderPayload = (payload: Payload): Omit<Booking, 'id' | 'userId'> | null => {
    const reference = firstString(payload.booking_reference, payload.order_id);
    if (!reference) return null;

    const slices: Payload[] = Array.isArray(payload.slices) ? payload.slices : [];
    const firstSlice = slices[0] || {};
    const lastSlice = slices[slices.length - 1] || firstSlice;

    const firstSegments: Payload[] = Array.isArray(firstSlice.segments) ? firstSlice.segments : [];
    const lastSegments: Payload[] = Array.isArray(lastSlice.segments) ? lastSlice.segments : [];
    const firstSegment = firstSegments[0] || {};
    const lastSegment = lastSegments[lastSegments.length - 1] || {};

    const originPlace = firstSlice.origin ?? firstSegment.origin;
    const destinationPlace = firstSlice.destination ?? firstSegment.destination;

    const origin = placeCode(originPlace);
    const destination = placeCode(destinationPlace);

    const startDate = isoDate(
        firstSlice.departure_date,
        firstSlice.departing_at,
        firstSegment.departing_at,
        firstSegment.departure_datetime,
        payload.check_in_date,
        payload.start_date,
    ) || new Date().toISOString();

    const endDate = isoDate(
        lastSlice.arrival_date,
        lastSlice.arriving_at,
        lastSegment.arriving_at,
        lastSegment.arrival_datetime,
        payload.check_out_date,
        payload.end_date,
    ) || startDate;

    const amount = Number(payload.total_amount);
    const passengerCount = Array.isArray(payload.passengers) ? payload.passengers.length : 0;

    const hasFlight = slices.length > 0 || Boolean(origin && destination);
    const hotelName = firstString(payload.hotel_name, payload.property_name);

    const title = hasFlight && origin && destination
        ? `${origin} → ${destination}`
        : hotelName || firstString(payload.title, `Booking ${reference}`);

    return {
        kind: hasFlight ? 'flight' : hotelName ? 'hotel' : 'activity',
        title,
        subtitle: passengerCount
            ? `${passengerCount} traveller${passengerCount === 1 ? '' : 's'}`
            : undefined,
        startDate,
        endDate,
        amount: Number.isFinite(amount) ? amount : 0,
        currency: firstString(payload.total_currency, 'USD').toUpperCase(),
        status: 'confirmed',
        reference,
        destination: placeName(destinationPlace) || hotelName || undefined,
    };
};
